/**
 * SchoolStatusCard.js
 *
 * Renders a status card based on the selected school/site. Adapted from the
 * onboarding site cards (SiteActiveCard / SiteInactiveCard / SiteNotFoundCard)
 * for the unauthenticated "find schools" page. Wording is driven by i18n so it
 * can be tuned for this context.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/solid';
import titleCase from '@/utils/titleCase';
import { SchoolStatus, getSchoolStatus } from '../utils/schoolStatus';

// Re-exported for existing consumers (e.g. FindSchools) that import the status
// helpers from this component. The implementation lives in ../utils/schoolStatus.
export { SchoolStatus, getSchoolStatus };

SiteAddress.propTypes = {
  siteData: PropTypes.object.isRequired,
};

/**
 * The selected site's name and address block, shared by every card.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} The rendered address block.
 */
function SiteAddress(props) {
  const { siteData } = props;
  return (
    <div className="text-base font-semibold text-gray-700 leading-[1.2] tracking-[-0.17px]">
      <p>{siteData.name}</p>
      {siteData.addressLine1 && <p>{titleCase(siteData.addressLine1)}</p>}
      {(siteData.city || siteData.state || siteData.zipCode) && (
        <p>{siteData.city} {siteData.state}{siteData.zipCode ? `, ${siteData.zipCode}` : ''}</p>
      )}
    </div>
  );
}

ActiveCard.propTypes = {
  siteData: PropTypes.object.isRequired,
};

/**
 * Green card: the site is already an active member of the network.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} The rendered active card.
 */
function ActiveCard(props) {
  const { siteData } = props;
  const { t } = useTranslation();
  return (
    <div className="bg-green-100 border border-cs-green-500 rounded-2xl flex gap-2 items-start px-4 pt-4 pb-5">
      <CheckCircleIcon className="w-[22px] h-[22px] text-cs-green-500 shrink-0 mt-0.5" />
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-4">
          <p className="font-semibold text-base text-gray-700 leading-[1.2] tracking-[-0.17px]">
            {t('find_schools.card.active_title')}
          </p>
          <p className="text-base text-gray-700 leading-[1.2] tracking-[-0.16px]">
            {t('find_schools.card.active_body')}
          </p>
        </div>
        <SiteAddress siteData={siteData} />
      </div>
    </div>
  );
}

InactiveCard.propTypes = {
  siteData: PropTypes.object.isRequired,
};

/**
 * Blue card: the site is in the database but not yet active.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} The rendered inactive card.
 */
function InactiveCard(props) {
  const { siteData } = props;
  const { t } = useTranslation();
  return (
    <div className="bg-cs-blue-100 border border-cs-blue-500 rounded-2xl flex gap-2 items-start px-4 pt-4 pb-5">
      <RocketLaunchIcon className="w-[22px] h-[22px] text-cs-blue-500 shrink-0 mt-0.5" />
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-4">
          <p className="font-semibold text-base text-gray-700 leading-[1.2] tracking-[-0.17px]">
            {t('find_schools.card.inactive_title')}
          </p>
          <p className="text-base text-gray-700 leading-[1.2] tracking-[-0.16px]">
            {t('find_schools.card.inactive_body_1')}
          </p>
        </div>
        <SiteAddress siteData={siteData} />
      </div>
    </div>
  );
}

NotFoundCard.propTypes = {
  siteData: PropTypes.object.isRequired,
};

/**
 * Blue card: the site isn't in the database yet.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} The rendered not-found card.
 */
function NotFoundCard(props) {
  const { siteData } = props;
  const { t } = useTranslation();
  return (
    <div className="bg-cs-blue-100 border border-cs-blue-500 rounded-2xl flex gap-2 items-start px-4 pt-4 pb-5">
      <RocketLaunchIcon className="w-[22px] h-[22px] text-cs-blue-500 shrink-0 mt-0.5" />
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-4">
          <p className="font-semibold text-base text-gray-700 leading-[1.2] tracking-[-0.17px]">
            {t('find_schools.card.not_found_title')}
          </p>
          <p className="text-base text-gray-700 leading-[1.2] tracking-[-0.16px]">
            {t('find_schools.card.not_found_body')}
          </p>
        </div>
        <SiteAddress siteData={siteData} />
      </div>
    </div>
  );
}

SchoolStatusCard.propTypes = {
  siteData: PropTypes.object.isRequired,
};

/**
 * Displays the appropriate status card for the selected school.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element|null} The rendered status card.
 */
export default function SchoolStatusCard(props) {
  const { siteData } = props;
  const status = getSchoolStatus(siteData);

  if (status === SchoolStatus.ACTIVE) return <ActiveCard siteData={siteData} />;
  if (status === SchoolStatus.INACTIVE) return <InactiveCard siteData={siteData} />;
  if (status === SchoolStatus.NOT_FOUND) return <NotFoundCard siteData={siteData} />;
  return null;
}
